import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Dialog from '@material-ui/core/Dialog';
import { fontFamily, fontSize } from '../../constants/text';
import { colors } from '../../constants/colors';
import history from '../../core/history';
import { isEmptyArray } from '../../core/util/array';
import { isTelemedLocation } from '../../core/util/location';
import { safeGet } from '../../core/util/object';
import { formatPhone, isEmptyString } from '../../core/util/string';
import { analyticsTrackEvent } from '../../core/analytics';
import {
  ACTION_CLICK_BOOK_IN_PERSON_MODAL,
  ACTION_CLICK_CALL_CLINIC_MODAL,
  ACTION_CLICK_CONTINUE_BOOK_TELEMED_MODAL,
  ACTION_OPEN_MODAL,
  BOOKING_WIDGET_TELEMED_MODAL_IN_PERSON_REDIRECT,
} from '../../core/analytics/events';

import Button, { ButtonVariations } from '../SolvPatternLibrary/Button';
import { apiGetJson } from '../../core/dapi';
import { getPhysicalLocationsInGroup } from '../../core/dapi/location';

const SmoothDialog = styled(Dialog).withConfig({ componentId: 'sc-1xyir37-0' })`
  -webkit-font-smoothing: antialiased;
`;

const TelemedModalContainer = styled.div.withConfig({ componentId: 'sc-1xyir37-1' })`
  padding: 40px;
  text-align: center;
`;

const TelemedModalHeader = styled.div.withConfig({ componentId: 'sc-1xyir37-2' })`
  font-size: ${fontSize.damnLarge};
  color: ${colors.squid};
  font-family: ${fontFamily.bold};
  margin-bottom: 15px;
`;

const TelemedModalText = styled.div.withConfig({ componentId: 'sc-1xyir37-3' })`
  margin-bottom: 10px;
`;

const TelemedModalBody = styled.div.withConfig({ componentId: 'sc-1xyir37-4' })`
  display: flex;
  flex-direction: column;
  margin: 15px;
  align-items: center;
  font-size: ${fontSize.default};
  color: ${colors.blueMonday};
`;

const TelemedModalButtonContainer = styled.div.withConfig({ componentId: 'sc-1xyir37-5' })`
  margin: 10px 0;
  font-family: ${fontFamily.book};
`;
const TelemedModalButton = styled(Button).withConfig({ componentId: 'sc-1xyir37-6' })`
  width: 230px;
`;

type Props = {
  closeTelemedModal: (...args: any[]) => any;
  location?: any;
};

const TelemedModal = (props: Props) => {
  const [hasAPhysicalLocationInGroup, setHasAPhysicalLocationInGroup] = useState(false);
  const { closeTelemedModal, location } = props;
  const groups = safeGet(location)('groups');
  const groupId = safeGet(groups)('0.group_id');

  useEffect(() => {
    if (!isEmptyString(groupId)) {
      apiGetJson(getPhysicalLocationsInGroup(groupId)).then((json: any) => {
        const response = safeGet(json)('data.results');
        if (!isEmptyArray(response)) setHasAPhysicalLocationInGroup(true);
      });
    }
  }, [groupId]);

  const getRedirectToMultilocationWidgetUrl = () =>
    `/book-group/${props.location.groups[0].group_id}`;

  const trackAnalytics = (action: any) => {
    const trackingProperties = {
      action,
      locationId: props.location.id,
    };
    analyticsTrackEvent(BOOKING_WIDGET_TELEMED_MODAL_IN_PERSON_REDIRECT, trackingProperties);
  };

  const handleBookVideoVisit = () => {
    trackAnalytics(ACTION_CLICK_CONTINUE_BOOK_TELEMED_MODAL);
    closeTelemedModal();
  };

  const handleBookInPersonVisit = () => {
    trackAnalytics(ACTION_CLICK_BOOK_IN_PERSON_MODAL);
    closeTelemedModal();
    history.push(getRedirectToMultilocationWidgetUrl());
  };

  const handleOnEnterModal = () => {
    trackAnalytics(ACTION_OPEN_MODAL);
  };

  const makePhoneCall = () => {
    trackAnalytics(ACTION_CLICK_CALL_CLINIC_MODAL);
    closeTelemedModal();

    if (safeGet(location)('phone')) {
      window.location.href = `tel:${location.phone}`;
    }
  };

  if (location && isTelemedLocation(location)) {
    const telephoneNumber = safeGet(location)('phone');
    return (
      <SmoothDialog maxWidth="md" onClose={closeTelemedModal} onEnter={handleOnEnterModal} open>
        <TelemedModalContainer>
          <TelemedModalHeader>Heads up!</TelemedModalHeader>

          <TelemedModalBody>
            <TelemedModalText>
              It looks like your symptoms would be
              <br />
              better treated in person.
            </TelemedModalText>
            {!hasAPhysicalLocationInGroup ? (
              <TelemedModalButtonContainer>
                <p>Call the clinic</p>

                <TelemedModalButton
                  onClick={makePhoneCall}
                  variation={ButtonVariations.pinkTertiary}
                >
                  +1&nbsp;{formatPhone(telephoneNumber)}
                </TelemedModalButton>
              </TelemedModalButtonContainer>
            ) : (
              <TelemedModalButton
                onClick={handleBookInPersonVisit}
                variation={ButtonVariations.pinkTertiary}
              >
                Book in person visit
              </TelemedModalButton>
            )}
            {!isEmptyArray(groups) && (
              <TelemedModalButtonContainer>
                <TelemedModalButton
                  onClick={handleBookVideoVisit}
                  variation={ButtonVariations.pinkTertiary}
                >
                  Continue to Book Online
                </TelemedModalButton>
              </TelemedModalButtonContainer>
            )}
          </TelemedModalBody>
        </TelemedModalContainer>
      </SmoothDialog>
    );
  }

  return <div />;
};

export default TelemedModal;
